import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementNode,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';

type SerializedAiDraftSectionNode = Spread<
  { contractId: string },
  SerializedElementNode
>;

export const AI_DRAFT_SECTION_ATTR = 'data-ai-draft';
/** 레거시 TinyMCE 템플릿 id — 레거시 작성 문서를 V2 에서 편집할 때도 같은 섹션으로 인식 */
const LEGACY_ID_PATTERN = /^ai_draft_(\d+)$/;

const readContractId = (domNode: HTMLElement): string | null => {
  const attr = domNode.getAttribute(AI_DRAFT_SECTION_ATTR);
  if (attr && /^\d+$/.test(attr)) return attr;
  const legacy = domNode.id.match(LEGACY_ID_PATTERN);
  return legacy ? legacy[1] : null;
};

/**
 * 계약 품의 AI 초안 섹션 컨테이너 — 계약별로 본문에 한 개. Lexical 은 일반 div 의 id/class 를
 * 버리기 때문에 교체·삭제 대상 식별자(contractId)를 노드로 보존. 마커 속성 없는 div 는 건드리지 않음
 */
export class AiDraftSectionNode extends ElementNode {
  __contractId: string;

  constructor(contractId: string, key?: NodeKey) {
    super(key);
    this.__contractId = contractId;
  }

  static getType(): string {
    return 'ai-draft-section';
  }

  static clone(node: AiDraftSectionNode): AiDraftSectionNode {
    return new AiDraftSectionNode(node.__contractId, node.__key);
  }

  getContractId(): string {
    return this.getLatest().__contractId;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const dom = document.createElement('div');
    dom.setAttribute(AI_DRAFT_SECTION_ATTR, this.__contractId);
    return dom;
  }

  updateDOM(prevNode: AiDraftSectionNode, dom: HTMLElement): boolean {
    if (prevNode.__contractId !== this.__contractId) {
      dom.setAttribute(AI_DRAFT_SECTION_ATTR, this.__contractId);
    }
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        const contractId = readContractId(domNode);
        if (!contractId) return null;
        return {
          conversion: (): DOMConversionOutput => ({
            node: $createAiDraftSectionNode(contractId),
          }),
          priority: 2,
        };
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.id = `ai_draft_${this.__contractId}`;
    element.setAttribute(AI_DRAFT_SECTION_ATTR, this.__contractId);
    return { element };
  }

  static importJSON(
    serializedNode: SerializedAiDraftSectionNode,
  ): AiDraftSectionNode {
    return $createAiDraftSectionNode(serializedNode.contractId);
  }

  exportJSON(): SerializedAiDraftSectionNode {
    return {
      ...super.exportJSON(),
      contractId: this.__contractId,
      type: 'ai-draft-section',
      version: 1,
    };
  }

  /** 내용을 다 지우면 빈 껍데기가 남지 않게 함께 제거 */
  canBeEmpty(): boolean {
    return false;
  }

  isInline(): boolean {
    return false;
  }
}

export function $createAiDraftSectionNode(
  contractId: string,
): AiDraftSectionNode {
  return $applyNodeReplacement(new AiDraftSectionNode(contractId));
}

export function $isAiDraftSectionNode(
  node: LexicalNode | null | undefined,
): node is AiDraftSectionNode {
  return node instanceof AiDraftSectionNode;
}
